import {
  first,
  forEach,
  size,
  uniq,
  filter,
  flatten,
  map,
  find,
  get,
  sortBy
} from "lodash";
import { DATE_TIME_FORMAT } from "@/utils/time";
import moment from "moment";
import { getFullName } from "@/utils/users";
import { formatDate } from "@/utils/time";
import { getDateRange } from "@/utils/time";
import { API_DATE_FORMAT } from "@/constants/common.js";

const ALL_USERS_AVAILABLE_DEFAULT_VAL = 1;

const INTERVIEW_COLORS = {
  VERY_HIGH: "#17D99B",
  HIGH: "#FFB800",
  MEDIUM: "#FFA000",
  LOW: "#EA0B40",
  SELECTED: "#4B5D69",
  UNSELECTED: "#fff"
};

export const getColorBySlotAvailability = (availableSlots, maxAvailable) => {
  const { VERY_HIGH, HIGH, MEDIUM, LOW } = INTERVIEW_COLORS;
  const percentageAvailibity = availableSlots / maxAvailable;
  switch (true) {
    case percentageAvailibity >= 0.75:
      return VERY_HIGH;
    case percentageAvailibity >= 0.5:
      return HIGH;
    case percentageAvailibity >= 0.25:
      return MEDIUM;
    default:
      return LOW;
  }
};

export const changeCalendarDaySelected = ({ date, selected }) => {
  const eventElement = first(
    document.querySelectorAll(`[data-date="${date}"]`)
  );
  if (eventElement) {
    const dayElement = eventElement.parentNode;
    selected
      ? dayElement.classList.add("day--selected")
      : dayElement.classList.remove("day--selected");
  }
};

export const removeEventsFromInactiveDayFields = () => {
  const dayElements = document.querySelectorAll(".v-calendar-weekly__day");
  forEach(dayElements, dayElement => {
    const areEventsAvailable = size(dayElement.querySelectorAll("[data-date]"));
    if (!areEventsAvailable) {
      dayElement.style.pointerEvents = "none";
    } else {
      dayElement.style.pointerEvents = "initial";
      dayElement.style.cursor = "pointer";
    }
  });
};

export const getAvailableSlotParamsForGivenMonth = ({
  date,
  users,
  allParticipantsAvailable = ALL_USERS_AVAILABLE_DEFAULT_VAL,
  bufferBeforeSlot,
  minimumSchedulingNotice,
  bufferAfterSlot
}) => {
  const monthStartDate = moment(date)
    .startOf("month")
    .format(DATE_TIME_FORMAT);
  const monthEndDate = moment(date)
    .endOf("month")
    .format(DATE_TIME_FORMAT);

  return {
    users,
    all_users_available: allParticipantsAvailable,
    start_date: monthStartDate,
    end_date: monthEndDate,
    ...(bufferBeforeSlot && { buffer_before_slot: bufferBeforeSlot }),
    ...(bufferAfterSlot && { buffer_after_slot: bufferAfterSlot }),
    ...(minimumSchedulingNotice && {
      minimum_scheduling_notice: minimumSchedulingNotice
    })
  };
};

export const getInterviewsParticipantsNames = (interviews, participantType) => {
  return uniq(
    flatten(
      map(interviews, interview =>
        map(getInterviewParticipants(interview, participantType), ({ user }) =>
          getFullName(user)
        )
      )
    )
  );
};

export const getInterviewParticipants = (interview, participantType) => {
  return filter(interview.participants, { participantType });
};

export const getCurrentParticipantId = ({ userId, participants }) => {
  return get(find(participants, ["user.id", userId]), "id");
};

export const getInterviewDateTimeRange = ({ startTime, endTime }) => {
  return `
    ${formatDate({
      date: startTime,
      startFormat: API_DATE_FORMAT,
      endFormat: "Do MMMM YYYY"
    })}, 
    ${getDateRange({
      startDate: startTime,
      endDate: endTime,
      format: "HH:mm"
    })}
  `;
};

export const getSlotsGroupedByDate = slots => {
  const sortedSlots = sortBy(slots, "startTime");
  return sortedSlots.reduce((formattedSlots, slot) => {
    const { startTime: startDate, endTime: endDate } = slot;
    const formattedDate = formatDate({ date: startDate });
    const formattedSlot = {
      label: getDateRange({ startDate, endDate, format: "HH:mm" }),
      value: slot
    };
    if (formattedSlots[formattedDate]) {
      formattedSlots[formattedDate] = [
        ...formattedSlots[formattedDate],
        formattedSlot
      ];
    } else {
      formattedSlots[formattedDate] = [formattedSlot];
    }
    return formattedSlots;
  }, {});
};

const getSlotsTimeRange = slotStartDate => {
  const startDate = moment(slotStartDate)
    .utc(false)
    .startOf("hour")
    .format(API_DATE_FORMAT);
  const endDate = moment(startDate)
    .add(1, "hours")
    .format(API_DATE_FORMAT);

  return getDateRange({
    startDate,
    endDate,
    format: "HH:mm"
  });
};

export const getSlotsGroupedByTimeRange = slots => {
  return slots.reduce((formattedSlots, slot) => {
    const { startTime: startDate, endTime: endDate } = slot;
    const timeRange = getSlotsTimeRange(startDate);
    const formattedSlot = {
      label: getDateRange({ startDate, endDate, format: "HH:mm" }),
      value: slot
    };
    if (formattedSlots[timeRange]) {
      formattedSlots[timeRange] = [...formattedSlots[timeRange], formattedSlot];
    } else {
      formattedSlots[timeRange] = [formattedSlot];
    }
    return formattedSlots;
  }, {});
};
